import React, { useMemo, useState } from "react";
import { Button, Card, Select, RestaurantBar, Plus } from "../../ui";
import { DASHBOARD_TEXT } from "../data/dashboardText";
import {
  billingStatusGroups,
  formatSarFromMinor,
} from "../data/subscriptionSummary";

const statusMapForOverview = (status) =>
  ["active", "فعال"].includes(String(status || "").toLowerCase())
    ? "active"
    : "inactive";
export function SummaryCard({ label, value, tone = "primary" }) {
  return (
    <Card
      className={`lud-dashboard-summary-card lud-dashboard-summary-card--${tone}`}
    >
      <strong>{value}</strong>
      <span>{label}</span>
    </Card>
  );
}

const countBy = (items = [], getValue) =>
  items.reduce((result, item) => {
    const value = getValue(item);
    const key = String(value || "unknown");
    result[key] = (result[key] || 0) + 1;
    return result;
  }, {});

const STATUS_LABELS = {
  active: ["فعال", "Active"],
  inProgress: ["جاري العمل", "In Progress"],
  inactive: ["غير نشط", "Inactive"],
  warning: ["قرب الانتهاء", "Expiring"],
  closed: ["مغلق", "Closed"],
  pending: ["معلق", "Pending"],
  awaitingSignature: ["بانتظار التوقيع", "Awaiting Signature"],
  awaitingContractReview: ["بانتظار مراجعة العقد", "Awaiting Contract Review"],
  approved: ["مقبول", "Approved"],
  rejected: ["مرفوض", "Rejected"],
  completed: ["مكتمل", "Completed"],
  canceled: ["ملغي", "Canceled"],
  unpaid: ["غير مدفوع", "Unpaid"],
  partial: ["مدفوع جزئي", "Partially Paid"],
  review: ["قيد المراجعة", "Under Review"],
  paid: ["مدفوع", "Paid"],
  new: ["جديد", "New"],
  startSubscription: ["ابدأ الاشتراك", "Start Subscription"],
  maintenance: ["صيانة", "Maintenance"],
  temporarilyClosed: ["مغلق مؤقت", "Temporarily Closed"],
  unknown: ["غير محدد", "Unknown"],
};

const getStatusLabel = (status, lang) =>
  STATUS_LABELS[status]?.[lang === "ar" ? 0 : 1] ||
  status ||
  (lang === "ar" ? "غير محدد" : "Unknown");

const BRANCH_STATUS_LABELS = {
  new: ["جديد", "New"],
  inProgress: ["جاري العمل", "In Progress"],
  active: ["فعال", "Active"],
  temporarilyClosed: ["مغلق مؤقت", "Temporarily Closed"],
  closed: ["مغلق", "Closed"],
  inactive: ["غير فعال", "Inactive"],
  maintenance: ["صيانة", "Maintenance"],
};

const getBranchStatusLabel = (status, lang) =>
  BRANCH_STATUS_LABELS[status]?.[lang === "ar" ? 0 : 1] ||
  getStatusLabel(status, lang);

function AnalyticsBreakdownCard({ title, total, groups, emptyText }) {
  return (
    <Card className="lud-admin-analytics-card">
      <div className="lud-admin-analytics-card-head">
        <span>{title}</span>
        <strong>{total}</strong>
      </div>
      <div className="lud-admin-analytics-breakdown">
        {groups.map((group) => (
          <div key={group.key}>
            <span>{group.label}</span>
            <strong>{group.value}</strong>
          </div>
        ))}
        {!groups.length && emptyText && (
          <div>
            <span>{emptyText}</span>
          </div>
        )}
      </div>
    </Card>
  );
}

// Box values for the billing stats: "…" while loading, "—" on failure,
// otherwise the server-aggregated figure (0 is a real value, not a fallback).
const billingValue = (billingStats, pick) =>
  billingStats?.status === "ready" && billingStats.data
    ? pick(billingStats.data)
    : billingStats?.status === "error"
      ? "—"
      : "…";

function AdminExtraAnalytics({ lang, dashboard, restaurants }) {
  const sources = dashboard.allStoreData || [];
  const branches = sources.flatMap((item) => item.branches || []);
  const applications = sources.flatMap((item) => item.applications || []);
  const requests = sources.flatMap((item) => item.requests || []);

  const restaurantStatuses = countBy(restaurants, (item) => item.status);
  const restaurantPackages = countBy(restaurants, (item) => item.package);
  const branchStatuses = countBy(branches, (item) => item.status);
  const applicationStatuses = countBy(applications, (item) => item.status);
  const requestStatuses = countBy(requests, (item) => item.status);

  const statusGroups = (counts, getLabel = getStatusLabel) =>
    Object.entries(counts).map(([key, value]) => ({
      key,
      label: getLabel(key, lang),
      value,
    }));

  const billingText = DASHBOARD_TEXT[lang].billingStats;
  const billingStats = dashboard.billingStats;
  const billingReady = billingStats?.status === "ready" && billingStats.data;
  const billingEmptyText =
    billingStats?.status === "error"
      ? billingText.error
      : billingReady
        ? DASHBOARD_TEXT[lang].noData
        : "";
  const subscriptionGroups = billingReady
    ? billingStatusGroups(
        billingStats.data.subscriptions.byStatus,
        billingText.subscriptionStatuses,
        {
          withoutSubscription:
            billingStats.data.subscriptions.withoutSubscription,
        },
      )
    : [];
  const invoiceGroups = billingReady
    ? billingStatusGroups(
        billingStats.data.invoices.byStatus,
        billingText.invoiceStatuses,
      )
    : [];

  const packageGroups = Object.entries(restaurantPackages).map(
    ([key, value]) => ({
      key: `package-${key}`,
      label:
        key === "unknown"
          ? lang === "ar"
            ? "بدون باقة"
            : "No Package"
          : `${lang === "ar" ? "باقة" : "Package"} ${key}`,
      value,
    }),
  );

  return (
    <div className="lud-admin-analytics-section">
      <h3>{lang === "ar" ? "تحليلات الحالات" : "Status Analytics"}</h3>
      <div className="lud-admin-analytics-grid">
        <AnalyticsBreakdownCard
          title={lang === "ar" ? "المطاعم" : "Restaurants"}
          total={restaurants.length}
          lang={lang}
          groups={[...statusGroups(restaurantStatuses), ...packageGroups]}
        />
        <AnalyticsBreakdownCard
          title={lang === "ar" ? "الفروع" : "Branches"}
          total={branches.length}
          lang={lang}
          groups={statusGroups(branchStatuses, getBranchStatusLabel)}
        />
        <AnalyticsBreakdownCard
          title={lang === "ar" ? "التطبيقات" : "Applications"}
          total={applications.length}
          lang={lang}
          groups={statusGroups(applicationStatuses)}
        />
        <AnalyticsBreakdownCard
          title={lang === "ar" ? "الإجراءات" : "Actions"}
          total={requests.length}
          lang={lang}
          groups={statusGroups(requestStatuses)}
        />
        <AnalyticsBreakdownCard
          title={billingText.subscriptions}
          total={billingValue(billingStats, (data) => data.subscriptions.count)}
          lang={lang}
          groups={subscriptionGroups}
          emptyText={billingEmptyText}
        />
        <AnalyticsBreakdownCard
          title={billingText.invoices}
          total={billingValue(billingStats, (data) => data.invoices.count)}
          lang={lang}
          groups={invoiceGroups}
          emptyText={billingEmptyText}
        />
      </div>
    </div>
  );
}
export function RestaurantsOverview({
  lang,
  selectedId,
  onSelect,
  onOpenPricing,
  dashboard,
  restaurants = [],
  isAdmin,
  // Present only for a customer who may add a store to their own account (restaurant.createOwn); the bar then ends with the action.
  onAddStore,
}) {
  const [filters, setFilters] = useState({
    city: "",
    status: "",
    package: "",
    search: "",
  });
  const filteredRestaurants = useMemo(() => {
    return restaurants.filter((restaurant) => {
      if (filters.city && restaurant.city !== filters.city) return false;
      if (filters.status && restaurant.status !== filters.status) return false;
      if (filters.package && restaurant.package !== filters.package)
        return false;
      const haystack =
        `${restaurant.id} ${restaurant.nameAr} ${restaurant.nameEn}`.toLowerCase();
      return haystack.includes(filters.search.toLowerCase());
    });
  }, [filters, restaurants]);
  const billingText = DASHBOARD_TEXT[lang].billingStats;
  const updateFilter = (key, value) =>
    setFilters((current) => ({ ...current, [key]: value }));
  const handleSelect = (restaurantId) => {
    onSelect(restaurantId);
    if (restaurantId) window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (
    <section className="lud-dashboard-card lud-restaurants-overview admin-only">
      {isAdmin && (
        <>
          <div className="lud-dashboard-section-title">
            <h2>
              {lang === "ar"
                ? "نظرة عامة على كل المطاعم"
                : "All Restaurants Overview"}
            </h2>
            <Button size="sm" appearance="soft" onClick={onOpenPricing}>
              {lang === "ar" ? "إدارة الأسعار والبنود" : "Pricing and items"}
            </Button>
          </div>

          <div className="lud-overview-kpis">
            <SummaryCard
              label={lang === "ar" ? "إجمالي المطاعم" : "Restaurants"}
              value={restaurants.length}
            />
            <SummaryCard
              label={lang === "ar" ? "إجمالي الفروع" : "Branches"}
              value={restaurants.reduce(
                (sum, item) => sum + Number(item.branches || 0),
                0,
              )}
            />
            <SummaryCard
              label={lang === "ar" ? "طلبات معلقة" : "Pending requests"}
              value={dashboard.allStoreData.reduce(
                (sum, item) => sum + Number(item.summary?.activeRequests || 0),
                0,
              )}
              tone="warning"
            />
            <SummaryCard
              label={
                lang === "ar" ? "اعتراضات هذا الشهر" : "Monthly objections"
              }
              value={dashboard.allStoreData.reduce(
                (sum, item) =>
                  sum +
                  (item.operations || []).filter(
                    (operation) => operation.status === "rejected",
                  ).length,
                0,
              )}
              tone="danger"
            />
            <SummaryCard
              label={lang === "ar" ? "مطاعم نشطة" : "Active restaurants"}
              value={
                restaurants.filter(
                  (item) => statusMapForOverview(item.status) === "active",
                ).length
              }
              tone="success"
            />
            <SummaryCard
              label={billingText.expiringSubscriptions}
              value={billingValue(
                dashboard.billingStats,
                (data) => data.subscriptions.expiringSoon,
              )}
              tone="warning"
            />
          </div>

          <div className="lud-overview-kpis lud-overview-kpis--financial">
            <SummaryCard
              label={
                lang === "ar" ? "طلبات جاري العمل" : "Requests in progress"
              }
              value={
                dashboard.requests.filter((item) => item.status === "pending")
                  .length
              }
              tone="warning"
            />
            <SummaryCard
              label={lang === "ar" ? "طلبات نشطة" : "Active requests"}
              value={
                dashboard.requests.filter((item) => item.status === "approved")
                  .length
              }
            />
            <SummaryCard
              label={billingText.invoiceCount}
              value={billingValue(
                dashboard.billingStats,
                (data) => data.invoices.count,
              )}
            />
            <SummaryCard
              label={billingText.invoiceValue}
              value={billingValue(dashboard.billingStats, (data) =>
                formatSarFromMinor(data.invoices.totalMinor),
              )}
            />
            <SummaryCard
              label={billingText.paidValue}
              value={billingValue(dashboard.billingStats, (data) =>
                formatSarFromMinor(data.invoices.paidMinor),
              )}
              tone="success"
            />
            <SummaryCard
              label={billingText.unpaidValue}
              value={billingValue(dashboard.billingStats, (data) =>
                formatSarFromMinor(data.invoices.unpaidMinor),
              )}
              tone="danger"
            />
          </div>

          <div className="lud-status-breakdown">
            <span>
              {lang === "ar" ? "التطبيقات" : "Applications"}:
              {lang === "ar" ? "فعال" : "Active"}{" "}
              {
                dashboard.applications.filter(
                  (item) => item.status === "active",
                ).length
              }{" "}
              ·{lang === "ar" ? "جاري العمل" : "In progress"}{" "}
              {
                dashboard.applications.filter(
                  (item) => item.status === "inProgress",
                ).length
              }{" "}
              ·{lang === "ar" ? "غير نشط" : "Inactive"}{" "}
              {
                dashboard.applications.filter(
                  (item) => item.status === "inactive",
                ).length
              }
            </span>
            <span>
              {lang === "ar" ? "الفروع" : "Branches"}:
              {lang === "ar" ? "جديد" : "New"}{" "}
              {
                dashboard.branches.filter((item) => item.status === "new")
                  .length
              }{" "}
              ·{lang === "ar" ? "جاري العمل" : "In progress"}{" "}
              {
                dashboard.branches.filter(
                  (item) => item.status === "inProgress",
                ).length
              }{" "}
              ·{lang === "ar" ? "فعال" : "Active"}{" "}
              {
                dashboard.branches.filter((item) => item.status === "active")
                  .length
              }{" "}
              ·{lang === "ar" ? "مغلق مؤقت" : "Temporarily closed"}{" "}
              {
                dashboard.branches.filter(
                  (item) => item.status === "temporarilyClosed",
                ).length
              }{" "}
              ·{lang === "ar" ? "مغلق" : "Closed"}{" "}
              {
                dashboard.branches.filter((item) => item.status === "closed")
                  .length
              }{" "}
              ·{lang === "ar" ? "غير فعال" : "Inactive"}{" "}
              {
                dashboard.branches.filter((item) => item.status === "inactive")
                  .length
              }{" "}
              ·{lang === "ar" ? "صيانة" : "Maintenance"}{" "}
              {
                dashboard.branches.filter(
                  (item) => item.status === "maintenance",
                ).length
              }
            </span>
          </div>

          <AdminExtraAnalytics
            lang={lang}
            dashboard={dashboard}
            restaurants={restaurants}
          />

          <div className="lud-dashboard-filters">
            <Select
              value={filters.city}
              onChange={(event) => updateFilter("city", event.target.value)}
              options={[
                {
                  value: "",
                  label: lang === "ar" ? "المدينة — الكل" : "All cities",
                },
                ...Array.from(
                  new Set(restaurants.map((item) => item.city).filter(Boolean)),
                ).map((value) => ({ value, label: value })),
              ]}
            />
            <Select
              value={filters.status}
              onChange={(event) => updateFilter("status", event.target.value)}
              options={[
                {
                  value: "",
                  label: lang === "ar" ? "الحالة — الكل" : "All statuses",
                },
                ...Array.from(
                  new Set(
                    restaurants.map((item) => item.status).filter(Boolean),
                  ),
                ).map((value) => ({
                  value,
                  label:
                    {
                      active: lang === "ar" ? "فعال" : "Active",
                      inProgress: lang === "ar" ? "جاري العمل" : "In Progress",
                      startSubscription:
                        lang === "ar" ? "ابدأ الاشتراك" : "Start Subscription",
                      closed: lang === "ar" ? "مغلق" : "Closed",
                      pending: lang === "ar" ? "قيد المراجعة" : "Pending",
                      new: lang === "ar" ? "جديد" : "New",
                    }[value] || value,
                })),
              ]}
            />
            <Select
              value={filters.package}
              onChange={(event) => updateFilter("package", event.target.value)}
              options={[
                {
                  value: "",
                  label: lang === "ar" ? "الباقة — الكل" : "All packages",
                },
                ...["L", "U", "D"].map((value) => ({ value, label: value })),
              ]}
            />
          </div>

          <div className="lud-restaurant-select">
            <Select
              value={selectedId || ""}
              onChange={(event) => handleSelect(event.target.value)}
              options={[
                {
                  value: "",
                  label: lang === "ar" ? "اختر المطعم" : "Select restaurant",
                },
                ...filteredRestaurants.map((restaurant) => ({
                  value: restaurant.id,
                  label: `${lang === "ar" ? restaurant.nameAr : restaurant.nameEn} · ${restaurant.id}`,
                })),
              ]}
            />
          </div>
        </>
      )}
      <RestaurantBar
        restaurants={filteredRestaurants}
        activeRestaurant={filteredRestaurants.findIndex(
          (restaurant) => restaurant.id === selectedId,
        )}
        onRestaurantChange={(_, restaurant) => handleSelect(restaurant.id)}
        getRestaurantKey={(restaurant) => restaurant.id}
        getRestaurantLabel={(restaurant) =>
          `${lang === "ar" ? restaurant.nameAr : restaurant.nameEn} · ${restaurant.id}`
        }
        actions={
          onAddStore ? (
            <Button
              size="sm"
              appearance="soft"
              icon={<Plus size={16} />}
              onClick={onAddStore}
              className="lud-add-store-button"
            >
              {lang === "ar" ? "إضافة متجر جديد" : "Add New Store"}
            </Button>
          ) : null
        }
      />
    </section>
  );
}
